import React, { useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';
import { API_URL } from '../utils/api';
import { BackButton } from '../components/BackButton';
import { ConfirmDialog, QuantityField } from '../components/ConfirmDialog';
import { Pictogram } from '../components/Pictogram';
import { DeathScreen } from '../components/DeathScreen';
import { paths } from '../navigation/paths';
import { Character, CharacterSheet, StatDistribution } from '../types';
import { readTablePlayers, type TablePlayer } from '../tableSession';
import { barPercent } from '../utils/avatars';
import { ClassMark } from '../components/Pictogram';
import type { Item } from '../types';
import {
  DAMAGE_MARKERS,
  armorPool,
  bonusSources,
  isArmorBroken,
  isConsumablePotion,
  isCurrencyItem,
  isPotionItem,
  itemArmorMax,
  itemArmorRemaining,
  itemDamageBonus,
  itemEffectTags,
  itemHealthBonus,
  itemKitChips,
  itemManaBonus,
  itemMovementBonus,
  itemPotionRestore,
  itemRangeBonus,
  parseEffectBonus,
  potionHasLastingEffect,
  potionRestoreSummary,
  stackInventoryItems,
  totalAttackDamage,
  totalMovement,
  vitalWithItemBonus,
} from '../utils/itemStats';

function withDistributedStats(char: Character, dist: StatDistribution): Character {
  if (!char.pending_level_up) return char;
  return {
    ...char,
    strength: (char.strength || 10) + (dist.strength || 0),
    dexterity: (char.dexterity || 10) + (dist.dexterity || 0),
    intelligence: (char.intelligence || 10) + (dist.intelligence || 0),
    constitution: (char.constitution || 10) + (dist.constitution || 0),
  };
}

function formatDelta(current: number, next: number): string | null {
  const delta = Math.round((next - current) * 10) / 10;
  if (delta === 0) return null;
  return delta > 0 ? `+${delta}` : `${delta}`;
}

function itemHint(item: Item, maxHp = 0, maxMana = 0): string {
  const description = item.description?.trim();
  if (description) return description;
  if (isCurrencyItem(item)) return 'kann ausgegeben werden';
  if (isPotionItem(item)) {
    const restore = itemPotionRestore(item);
    if (restore.healthPercent > 0 || restore.manaPercent > 0) {
      const summary = potionRestoreSummary(item, maxHp, maxMana);
      return [summary.healthLabel, summary.manaLabel].filter(Boolean).join(', ');
    }
    if (restore.health && restore.mana) return 'stellt Leben und Mana wieder her';
    if (restore.health) return 'stellt Leben wieder her';
    if (restore.mana) return 'stellt Mana wieder her';
  }
  const tags = itemEffectTags(item);
  const parts: string[] = [];
  if (tags.effectDamage !== 0 || (item.strength_bonus || 0) !== 0 || (item.damage_bonus || 0) !== 0) parts.push('Schaden');
  if (tags.effectMove !== 0) parts.push('Bewegung');
  if (tags.effectRange !== 0) parts.push('Reichweite');
  if ((item.dexterity_bonus || 0) !== 0) parts.push('Geschick');
  if ((item.intelligence_bonus || 0) !== 0) parts.push('Intelligenz');
  if ((item.constitution_bonus || 0) !== 0) parts.push('Konstitution');
  if ((item.health_bonus || 0) !== 0) parts.push('Leben');
  if (parts.length === 0) return item.effect?.trim() || '';
  if (parts.length === 1) return parts[0];
  return `${parts.slice(0, -1).join(', ')} und ${parts[parts.length - 1]}`;
}

function ValueChange({ current, next }: { current: number; next: number }) {
  const delta = formatDelta(current, next);
  if (!delta) return <>{next}</>;
  return (
    <>
      <span className="stat-was">{current}</span>
      <span className="stat-arrow">→</span>
      {next}
      <span className="stat-delta">{delta}</span>
    </>
  );
}

const EMPTY_STATS: StatDistribution = { strength: 0, dexterity: 0, intelligence: 0, constitution: 0 };

function AddPlayerControl() {
  const { addCompanion } = useApp();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');

  if (!open) {
    return (
      <button type="button" className="medieval-button" onClick={() => setOpen(true)}>
        Weiteren Spieler
      </button>
    );
  }

  return (
    <form
      className="companion-form"
      onSubmit={async (event) => {
        event.preventDefault();
        if (typeof addCompanion === 'function') await addCompanion(name);
        setName('');
        setOpen(false);
      }}
    >
      <input
        className="companion-input"
        aria-label="Name des weiteren Spielers"
        value={name}
        onChange={(event) => setName(event.target.value)}
        placeholder="Name des Spielers"
      />
      <button type="submit" className="medieval-button">Anzeigen</button>
      <button type="button" className="medieval-button" onClick={() => setOpen(false)}>Abbrechen</button>
    </form>
  );
}

function SheetView({
  data,
  actions,
  playerLabel,
  headerExtra,
  hideSessionButtons,
}: {
  data?: CharacterSheet | null;
  actions?: {
    statDistribution: StatDistribution;
    handleStatDecrease: (stat: 'strength' | 'dexterity' | 'intelligence' | 'constitution') => void;
    handleStatIncrease: (stat: 'strength' | 'dexterity' | 'intelligence' | 'constitution') => void;
    handleCommitLevelUp: () => void;
    handleUseItem: (characterItemId: number, count?: number) => void;
  };
  playerLabel?: string;
  headerExtra?: React.ReactNode;
  hideSessionButtons?: boolean;
}) {
  const [tooltipPopup, setTooltipPopup] = useState<{ content: string } | null>(null);
  const [spendStack, setSpendStack] = useState<{ item: Item; count: number } | null>(null);
  const [spendQty, setSpendQty] = useState(1);
  const app = useApp();
  const characterSheet = data === undefined ? app.characterSheet : data;
  const handlePlayerLogout = app.handlePlayerLogout;
  const calculateDamage = app.calculateDamage;
  const statDistribution = actions?.statDistribution ?? app.statDistribution;
  const handleStatDecrease = actions?.handleStatDecrease ?? app.handleStatDecrease;
  const handleStatIncrease = actions?.handleStatIncrease ?? app.handleStatIncrease;
  const handleCommitLevelUp = actions?.handleCommitLevelUp ?? app.handleCommitLevelUp;
  const handleUseItem = actions?.handleUseItem ?? app.handleUseItem;
  const evaluateFormulasInText = app.evaluateFormulasInText;

  if (!characterSheet) return <div>Lade...</div>;

  const char = characterSheet.character;
  const previewChar = withDistributedStats(char, statDistribution);
  const equippedItems = characterSheet.items || [];
  const activeEffects = characterSheet.effects || char.effects || [];
  const sources = bonusSources({ items: equippedItems, effects: activeEffects });
  const formulaHp = char.health_formula ? calculateDamage(char.health_formula, char, '50+constitution*5') : (char.max_health || 100);
  const previewFormulaHp = char.health_formula ? calculateDamage(char.health_formula, previewChar, '50+constitution*5') : formulaHp;
  const formulaMana = char.mana_formula ? calculateDamage(char.mana_formula, char, '20+intelligence*3') : (char.max_mana || 50);
  const previewFormulaMana = char.mana_formula ? calculateDamage(char.mana_formula, previewChar, '20+intelligence*3') : formulaMana;
  const lifeBonus = itemHealthBonus(sources);
  const manaBonus = itemManaBonus(sources);
  const hp = vitalWithItemBonus(char.health, formulaHp, lifeBonus);
  const previewHp = vitalWithItemBonus(char.health, previewFormulaHp, lifeBonus);
  const mp = vitalWithItemBonus(char.mana || 0, formulaMana, manaBonus);
  const previewMp = vitalWithItemBonus(char.mana || 0, previewFormulaMana, manaBonus);
  const maxHp = hp.max;
  const previewMaxHp = previewHp.max;
  const maxMana = mp.max;
  const previewMaxMana = previewMp.max;
  const healthPercent = barPercent(hp.current, maxHp);
  const manaPercent = barPercent(mp.current, maxMana);
  const armor = armorPool(sources);
  const armorPercent = barPercent(armor.current, armor.max);
  const baseDamage = calculateDamage(char.damage_formula, char);
  const previewBaseDamage = calculateDamage(char.damage_formula, previewChar);
  const damageBonus = itemDamageBonus(sources);
  const totalDamage = totalAttackDamage(baseDamage, sources);
  const previewTotalDamage = totalAttackDamage(previewBaseDamage, sources);
  const baseMove = calculateDamage(char.movement_formula, char);
  const previewBaseMove = calculateDamage(char.movement_formula, previewChar);
  const moveBonus = itemMovementBonus(sources);
  const totalMove = totalMovement(baseMove, sources);
  const previewTotalMove = totalMovement(previewBaseMove, sources);
  const rangeBonus = itemRangeBonus(sources);
  const remainingPoints = (char.pending_skill_points || 0) - Object.values(statDistribution).reduce((s, v) => s + v, 0);
  const spentPoints = Object.values(statDistribution).reduce((s, v) => s + v, 0);
  const gearItems = equippedItems.filter(item => !isConsumablePotion(item));
  const potionItems = equippedItems.filter(item => isConsumablePotion(item));
  const stackedGear = stackInventoryItems(gearItems);
  const isDefeated = (char.health || 0) <= 0;

  const potionIsFull = (item: Item) => {
    if (potionHasLastingEffect(item) && !isPotionItem(item)) return false;
    const restore = potionRestoreSummary(item, maxHp, maxMana);
    const hpFull = restore.health <= 0 || hp.current >= maxHp;
    const manaFull = restore.mana <= 0 || mp.current >= maxMana;
    return hpFull && manaFull;
  };

  const spendGold = (item: Item, count: number) => {
    if (!item.character_item_id) return;
    if (count <= 1) {
      handleUseItem(item.character_item_id, 1);
      return;
    }
    setSpendQty(1);
    setSpendStack({ item, count });
  };

  const renderItemCard = (item: Item, idx: number, potion: boolean, count = 1) => {
    const tags = itemEffectTags(item);
    const broken = isArmorBroken(item);
    const hint = itemHint(item, maxHp, maxMana);
    const gold = isCurrencyItem(item);
    const summary = potion ? potionRestoreSummary(item, maxHp, maxMana) : null;
    const previewSummary = potion ? potionRestoreSummary(item, previewMaxHp, previewMaxMana) : null;
    const itemIconMatch = !item.icon_url && item.name.match(/^(\p{Emoji}+)\s+(.+)$/u);
    const itemTooltip = [
      item.name,
      item.description && `Beschreibung: ${item.description}`,
      item.effect && `Effekt: ${item.effect}`,
      summary?.healthLabel,
      summary?.manaLabel,
      previewSummary && summary && previewSummary.health !== summary.health
        ? `Mit neuen Attributen: ${previewSummary.healthLabel}`
        : '',
      previewSummary && summary && previewSummary.mana !== summary.mana
        ? `Mit neuen Attributen: ${previewSummary.manaLabel}`
        : '',
    ].filter(Boolean).join('\n');
    const full = potion && potionIsFull(item);
    return (
      <div
        key={item.character_item_id || `${item.id}-${idx}`}
        className={`item-card item-card-compact${potion ? ' item-card-potion' : ''}${gold ? ' item-card-gold' : ''}${broken ? ' item-card-broken' : ''}`}
        title={itemTooltip || undefined}
        onClick={() => itemTooltip && setTooltipPopup({ content: itemTooltip })}
      >
        <div className="item-sheet-face">
          <div className="item-icon-compact">
            <Pictogram iconUrl={item.icon_url} type={item.type} name={item.name} kind="item" />
          </div>
          <span className="item-copy">
            <span className="item-name-compact">
              <span className="item-name-text">{itemIconMatch ? itemIconMatch[2] : item.name}</span>
              {count > 1 && <span className="stack-badge" aria-label={`${count} Stück`}>×{count}</span>}
            </span>
            {hint && <span className="item-hint">{hint}</span>}
          </span>
          {tags.hasAny && (
            <div className="item-bonuses-compact">
              {summary && summary.health > 0 && (
                <span>
                  💚+{summary.health}
                  {previewSummary && previewSummary.health !== summary.health && (
                    <>
                      <span className="stat-arrow">→</span>
                      +{previewSummary.health}
                    </>
                  )}
                </span>
              )}
              {summary && summary.mana > 0 && (
                <span>
                  ✨+{summary.mana}
                  {previewSummary && previewSummary.mana !== summary.mana && (
                    <>
                      <span className="stat-arrow">→</span>
                      +{previewSummary.mana}
                    </>
                  )}
                </span>
              )}
              {!potion && tags.healthRestore > 0 && <span>💚+{tags.healthRestore}</span>}
              {!potion && tags.manaRestore > 0 && <span>✨+{tags.manaRestore}</span>}
              {potion && tags.effectDamage > 0 && <span>⚔+{tags.effectDamage}</span>}
              {potion && tags.effectMove > 0 && <span>➤+{tags.effectMove}</span>}
              {potion && tags.effectRange > 0 && <span>🏹+{tags.effectRange}</span>}
              {!potion && itemKitChips(item).filter(chip => chip.label !== 'Kaputt').map(chip => (
                <span key={`${chip.kind}-${chip.label}`}>{chip.icon} {chip.label}</span>
              ))}
            </div>
          )}
          {broken && <span className="item-sheet-stamp">Kaputt</span>}
        </div>
        {potion && (
          <button
            type="button"
            className="item-use-btn"
            disabled={full || !item.character_item_id}
            onClick={(event) => {
              event.stopPropagation();
              if (item.character_item_id) handleUseItem(item.character_item_id);
            }}
          >
            {full ? 'Voll' : 'Trinken'}
          </button>
        )}
        {gold && (
          <button
            type="button"
            className="item-use-btn"
            disabled={!item.character_item_id}
            onClick={(event) => {
              event.stopPropagation();
              spendGold(item, count);
            }}
          >
            Ausgeben
          </button>
        )}
      </div>
    );
  };

  return (
    <div className={`character-sheet medieval compact-sheet${isDefeated ? ' is-defeated' : ''}`}>
      <header className="sheet-header">
        <div>
          <h1>
            <ClassMark klass={char.class} icon={app.classes?.find(c => c.name === char.class)?.icon} className="sheet-class-icon" /> {char.name}
            <span className="level-emblem" title="Level"><span className="level-emblem-icon">⭐</span> {char.level}</span>
            {playerLabel && <span className="sheet-player-name">{playerLabel}</span>}
          </h1>
          <p className="character-class">{char.class}</p>
        </div>
        <div className="header-buttons">
          {headerExtra}
          {!hideSessionButtons && (
            <BackButton fallback={paths.play} className="medieval-button">
              <span aria-hidden>←</span>
              <span className="btn-extra"> Zurück</span>
            </BackButton>
          )}
          {!hideSessionButtons && (
            <button className="medieval-button" onClick={handlePlayerLogout}>
              Abmelden
            </button>
          )}
        </div>
      </header>

      {isDefeated && (
        <DeathScreen name={char.name} items={potionItems} onDrink={handleUseItem} />
      )}

      <div className="sheet-dashboard" aria-hidden={isDefeated || undefined}>
        <section className="sheet-panel sheet-core">
          <div
            className="vital-bar clickable"
            onClick={() => char.health_formula && setTooltipPopup({
              content: [
                '📐 Max-Leben Berechnung',
                `Formel: ${char.health_formula}`,
                `= ${maxHp} Max-Leben`,
                previewMaxHp !== maxHp ? `Mit neuen Attributen: ${previewMaxHp} (${formatDelta(maxHp, previewMaxHp)})` : '',
              ].filter(Boolean).join('\n'),
            })}
          >
            <div className={`combat-bar combat-hp ${healthPercent > 0 && healthPercent <= 30 ? 'is-critical' : ''}`}>
              <div className="combat-bar-fill combat-hp-fill" style={{ width: `${healthPercent}%` }}></div>
              <span>
                ❤️ Leben {hp.current}/{previewMaxHp}
                {previewMaxHp !== maxHp && <span className="stat-delta">{formatDelta(maxHp, previewMaxHp)}</span>}
                {char.health_formula ? ' 📐' : ''}
              </span>
            </div>
          </div>

          {armor.max > 0 && (
            <div
              className="vital-bar clickable"
              onClick={() => setTooltipPopup({
                content: [
                  '🛡 Rüstung ist Zusatzleben',
                  `Aktuell ${armor.current} von ${armor.max}.`,
                  'Schaden wird abgezogen: zuerst Buffs, dann das Rüstungsteil mit dem niedrigsten Wert, danach Leben.',
                  'Ist ein Teil auf 0, ist es kaputt. Der Spielleiter kann es reparieren.',
                ].join('\n'),
              })}
            >
              <div className="combat-bar combat-armor">
                <div className="combat-bar-fill combat-armor-fill" style={{ width: `${armorPercent}%` }}></div>
                <span>🛡 Rüstung {armor.current}/{armor.max}</span>
              </div>
            </div>
          )}

          <div
            className="vital-bar clickable"
            onClick={() => char.mana_formula && setTooltipPopup({
              content: [
                '📐 Max-Mana Berechnung',
                `Formel: ${char.mana_formula}`,
                `= ${maxMana} Max-Mana`,
                previewMaxMana !== maxMana ? `Mit neuen Attributen: ${previewMaxMana} (${formatDelta(maxMana, previewMaxMana)})` : '',
              ].filter(Boolean).join('\n'),
            })}
          >
            <div className="combat-bar combat-mana">
              <div className="combat-bar-fill combat-mana-fill" style={{ width: `${manaPercent}%` }}></div>
              <span>
                ✨ Mana {mp.current}/{previewMaxMana}
                {previewMaxMana !== maxMana && <span className="stat-delta">{formatDelta(maxMana, previewMaxMana)}</span>}
                {char.mana_formula ? ' 📐' : ''}
              </span>
            </div>
          </div>

          <div className="combat-stats sheet-combat-stats">
            {char.damage_formula && (
              <button
                type="button"
                className="combat-stat sheet-stat"
                onClick={() => setTooltipPopup({
                  content: [
                    '📐 Schadensberechnung',
                    `Formel: ${char.damage_formula} = ${baseDamage}`,
                    damageBonus > 0 ? `Items: +${damageBonus} Schaden` : 'Keine Item-Boni',
                    ...equippedItems
                      .map(item => {
                        const extra = (item.strength_bonus || 0) + (item.damage_bonus || 0) + parseEffectBonus(item.effect, DAMAGE_MARKERS);
                        return extra > 0 ? `• ${item.name}: +${extra}` : '';
                      })
                      .filter(Boolean),
                    `= ${totalDamage}`,
                    previewTotalDamage !== totalDamage ? `Mit neuen Attributen: ${previewTotalDamage} (${formatDelta(totalDamage, previewTotalDamage)})` : '',
                  ].filter(Boolean).join('\n'),
                })}
              >
                <span className="sheet-stat-label">⚔ Schaden</span>
                <strong className="sheet-stat-value">
                  <ValueChange current={totalDamage} next={previewTotalDamage} />
                  {damageBonus > 0 && <span className="stat-bonus">+{damageBonus}</span>}
                </strong>
              </button>
            )}
            {char.movement_formula && (
              <button
                type="button"
                className="combat-stat sheet-stat"
                onClick={() => setTooltipPopup({
                  content: [
                    '📐 Bewegungspunkte',
                    `Formel: ${char.movement_formula} = ${baseMove}`,
                    moveBonus > 0 ? `Items: +${moveBonus} Bewegung` : 'Keine Item-Boni',
                    `💪 ${previewChar.strength || 10} · 🎯 ${previewChar.dexterity || 10} · 🧠 ${previewChar.intelligence || 10} · ❤️ ${previewChar.constitution || 10}`,
                    `= ${totalMove}`,
                    previewTotalMove !== totalMove ? `Mit neuen Attributen: ${previewTotalMove} (${formatDelta(totalMove, previewTotalMove)})` : '',
                  ].filter(Boolean).join('\n'),
                })}
              >
                <span className="sheet-stat-label">➤ Bewegung</span>
                <strong className="sheet-stat-value">
                  <ValueChange current={totalMove} next={previewTotalMove} />
                  {moveBonus > 0 && <span className="stat-bonus">+{moveBonus}</span>}
                </strong>
              </button>
            )}
            {rangeBonus > 0 && (
              <div className="combat-stat sheet-stat">
                <span className="sheet-stat-label">🏹 Reichweite</span>
                <strong className="sheet-stat-value">+{rangeBonus}</strong>
              </div>
            )}
          </div>

          <div className={`stats-section ${char.pending_level_up ? 'levelup-active' : ''}`}>
            <div className="stats-header">
              <h2>Attribute</h2>
              {char.pending_level_up && (
                <div className="levelup-indicator">
                  <span className="levelup-badge">⬆️ Level Up</span>
                  <span className="points-remaining">{remainingPoints} übrig</span>
                </div>
              )}
            </div>
            <div className="stats-grid">
              {(['strength', 'dexterity', 'intelligence', 'constitution'] as const).map(stat => (
                <div key={stat} className={`stat-item combat-stat-tile ${char.pending_level_up ? 'stat-editable' : ''}`}>
                  <span className="stat-icon">{stat === 'strength' ? '💪' : stat === 'dexterity' ? '🎯' : stat === 'intelligence' ? '🧠' : '❤️'}</span>
                  <span className="stat-name">{stat === 'strength' ? 'Staerke' : stat === 'dexterity' ? 'Geschick' : stat === 'intelligence' ? 'Intelligenz' : 'Konstitution'}</span>
                  {char.pending_level_up ? (
                    <div className="stat-levelup-controls">
                      <button className="stat-btn-inline decrease" onClick={() => handleStatDecrease(stat)} disabled={statDistribution[stat] === 0}>-</button>
                      <span className="stat-value">
                        {(char[stat] || 10) + statDistribution[stat]}
                        {statDistribution[stat] > 0 && <span className="stat-bonus">+{statDistribution[stat]}</span>}
                      </span>
                      <button className="stat-btn-inline increase" onClick={() => handleStatIncrease(stat)} disabled={Object.values(statDistribution).reduce((s, v) => s + v, 0) >= (char.pending_skill_points || 0)}>+</button>
                    </div>
                  ) : (
                    <span className="stat-value">{char[stat] || 10}</span>
                  )}
                </div>
              ))}
            </div>
            {char.pending_level_up && (
              <div className="levelup-confirm-area">
                <div className="levelup-preview" aria-live="polite">
                  <span className="levelup-preview-label">Vorschau</span>
                  <span>❤️ <ValueChange current={maxHp} next={previewMaxHp} /></span>
                  <span>✨ <ValueChange current={maxMana} next={previewMaxMana} /></span>
                  <span>⚔ <ValueChange current={totalDamage} next={previewTotalDamage} /></span>
                  <span>➤ <ValueChange current={totalMove} next={previewTotalMove} /></span>
                </div>
                <button
                  className="commit-levelup-button"
                  onClick={handleCommitLevelUp}
                  disabled={spentPoints !== (char.pending_skill_points || 0)}
                >
                  ✅ Level Up Bestaetigen ({spentPoints}/{char.pending_skill_points})
                </button>
              </div>
            )}
          </div>
        </section>

        <section className="sheet-panel sheet-gear">
          {characterSheet.items.length === 0 ? (
            <div className="sheet-gear-block">
              <h2>Items (0)</h2>
              <p className="no-content">Keine Items vorhanden</p>
            </div>
          ) : (
            <>
              {gearItems.length > 0 && (
                <div className="sheet-gear-block">
                  <h2>Ausrüstung ({gearItems.length})</h2>
                  <div className="items-grid items-grid-compact">
                    {stackedGear.map(({ item, count }, idx) => renderItemCard(item, idx, false, count))}
                  </div>
                </div>
              )}
              {potionItems.length > 0 && (
                <div className="sheet-gear-block">
                  <h2>Tränke ({potionItems.length})</h2>
                  <div className="items-grid items-grid-compact">
                    {stackInventoryItems(potionItems).map(({ item, count }, idx) => renderItemCard(item, idx, true, count))}
                  </div>
                </div>
              )}
              {activeEffects.length > 0 && (
                <div className="sheet-gear-block">
                  <h2>Aktive Effekte ({activeEffects.length})</h2>
                  <div className="items-grid items-grid-compact">
                    {activeEffects.map((effect, idx) => {
                      const effectArmor = itemArmorMax({ ...effect, type: effect.type || 'Effekt' });
                      const effectLeft = itemArmorRemaining({ ...effect, type: effect.type || 'Effekt' });
                      const effectHint = effect.effect || 'wirkt, bis der Spielleiter ihn beendet';
                      const armorHint = effectArmor <= 0
                        ? ''
                        : effectLeft <= 0
                          ? 'Rüstung aufgebraucht'
                          : `Rüstung ${effectLeft}/${effectArmor}`;
                      return (
                      <div key={effect.id || idx} className="item-card item-card-compact item-card-effect">
                        <div className="item-icon-compact">
                          <Pictogram iconUrl={effect.icon_url} type="Trank" kind="item" />
                        </div>
                        <span className="item-copy">
                          <span className="item-name-compact">
                            <span className="item-name-text">{effect.name}</span>
                          </span>
                          <span className="item-hint">{[effectHint, armorHint].filter(Boolean).join(' · ')}</span>
                        </span>
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          <div className="sheet-gear-block">
            <h2>Faehigkeiten ({characterSheet.spells.length})</h2>
            {characterSheet.spells.length === 0 ? (
              <p className="no-content">Keine Faehigkeiten vorhanden</p>
            ) : (
              <div className="abilities-list abilities-list-compact">
                {characterSheet.spells.map(spell => {
                  const evaluatedEffect = evaluateFormulasInText(spell.effect, {
                    ...char,
                    items: equippedItems,
                    effects: activeEffects,
                    basisschaden: baseDamage,
                    schaden: totalDamage,
                  });
                  const previewEffect = evaluateFormulasInText(spell.effect, {
                    ...previewChar,
                    items: equippedItems,
                    effects: activeEffects,
                    basisschaden: previewBaseDamage,
                    schaden: previewTotalDamage,
                  });
                  const effectChanged = Boolean(char.pending_level_up && previewEffect && previewEffect !== evaluatedEffect);
                  const spellIconMatch = !spell.icon_url && spell.name.match(/^(\p{Emoji}+)\s+(.+)$/u);
                  const spellTooltip = [
                    spell.name,
                    `✨ ${spell.mana_cost} Mana`,
                    spell.description,
                    spell.effect && `Effekt: ${evaluatedEffect}`,
                    effectChanged ? `Mit neuen Attributen: ${previewEffect}` : '',
                  ].filter(Boolean).join('\n');
                  return (
                    <div key={spell.id} className="ability-card ability-card-compact" title={spellTooltip || undefined} onClick={() => spellTooltip && setTooltipPopup({ content: spellTooltip })}>
                      <div className="ability-icon-compact">
                        <Pictogram iconUrl={spell.icon_url} name={spell.name} kind="spell" />
                      </div>
                      <span className="ability-copy">
                        <span className="ability-name-compact">{spellIconMatch ? spellIconMatch[2] : spell.name}</span>
                        {spell.effect && (
                          <span className={`ability-effect-compact${effectChanged ? ' is-preview' : ''}`}>
                            {effectChanged ? (
                              <>
                                <span className="stat-was">{evaluatedEffect}</span>
                                <span className="stat-arrow">→</span>
                                {previewEffect}
                              </>
                            ) : previewEffect}
                          </span>
                        )}
                      </span>
                      <span className="ability-mana">{spell.mana_cost} Mana</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {char.special_ability && (
            <div className="sheet-gear-block">
              <h2>Spezialfaehigkeit</h2>
              <p className="special-card compact-line">{char.special_ability}</p>
            </div>
          )}
        </section>
      </div>

      {tooltipPopup && (
        <div className="tooltip-popup-overlay" onClick={() => setTooltipPopup(null)} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Escape' && setTooltipPopup(null)} aria-label="Schliessen">
          <div className="tooltip-popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="tooltip-popup-body">
              {tooltipPopup.content.split('\n').map((line, i) => (
                <p key={i}>{line}</p>
              ))}
            </div>
            <button type="button" className="tooltip-popup-close" onClick={() => setTooltipPopup(null)}>Schliessen</button>
          </div>
        </div>
      )}

      {spendStack && (
        <ConfirmDialog
          title={`${spendStack.item.name} ausgeben?`}
          message={`Wie viel „${spendStack.item.name}“ ausgeben?`}
          confirmLabel={spendQty > 1 ? `${spendQty} ausgeben` : 'Ausgeben'}
          onCancel={() => setSpendStack(null)}
          onConfirm={() => {
            if (spendStack.item.character_item_id) {
              handleUseItem(spendStack.item.character_item_id, spendQty);
            }
            setSpendStack(null);
          }}
        >
          <QuantityField value={spendQty} max={spendStack.count} onChange={setSpendQty} />
        </ConfirmDialog>
      )}
    </div>
  );
}

function MateSheet({
  player,
  sheet,
  onReload,
}: {
  player: TablePlayer;
  sheet: CharacterSheet;
  onReload: () => void;
}) {
  const [statDistribution, setStatDistribution] = useState<StatDistribution>(EMPTY_STATS);
  const char = sheet.character;

  const changeStat = (stat: keyof StatDistribution, delta: number) => {
    const spent = Object.values(statDistribution).reduce((sum, value) => sum + value, 0);
    const available = char.pending_skill_points || 0;
    if (delta > 0 && spent >= available) return;
    if (delta < 0 && statDistribution[stat] <= 0) return;
    setStatDistribution((current) => ({ ...current, [stat]: current[stat] + delta }));
  };

  const handleUseItem = async (characterItemId: number, count = 1) => {
    const response = await fetch(`${API_URL}/api/players/${player.id}/use-item`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ character_item_id: characterItemId, count }),
    });
    if (response.ok) onReload();
  };

  const handleCommitLevelUp = async () => {
    const response = await fetch(`${API_URL}/api/players/${player.id}/commit-level-up`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ stat_increases: statDistribution }),
    });
    if (response.ok) {
      setStatDistribution(EMPTY_STATS);
      onReload();
    }
  };

  return (
    <SheetView
      data={sheet}
      playerLabel={player.name}
      hideSessionButtons
      actions={{
        statDistribution,
        handleStatDecrease: (stat) => changeStat(stat, -1),
        handleStatIncrease: (stat) => changeStat(stat, 1),
        handleCommitLevelUp,
        handleUseItem,
      }}
    />
  );
}

export function CharacterSheetPage() {
  const { characterSheet, playerId, playerName, handlePlayerLogout } = useApp();
  const [players, setPlayers] = useState<TablePlayer[]>(() => readTablePlayers());
  const [sheets, setSheets] = useState<Record<number, CharacterSheet>>({});
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const sync = () => setPlayers(readTablePlayers());
    window.addEventListener('table-players', sync);
    return () => window.removeEventListener('table-players', sync);
  }, []);

  useEffect(() => {
    if (players.length < 2) return undefined;
    let cancel = false;
    const load = async () => {
      const next: Record<number, CharacterSheet> = {};
      for (const player of players) {
        try {
          const response = await fetch(`${API_URL}/api/players/${player.id}/character`);
          if (response.ok) next[player.id] = await response.json();
        } catch {
          // Ein Held ohne Bogen bleibt in der Auswahl.
        }
      }
      if (!cancel) setSheets(next);
    };
    load();
    const timer = window.setInterval(load, 5000);
    return () => {
      cancel = true;
      window.clearInterval(timer);
    };
  }, [players, reloadToken]);

  const mates = players.filter((player) => player.id !== playerId && sheets[player.id]);
  if (mates.length === 0 || !characterSheet) {
    return <SheetView headerExtra={<AddPlayerControl />} playerLabel={playerName || undefined} />;
  }

  const primary = players.find((player) => player.id === playerId);

  return (
    <div className="player-table-page">
      <div className="player-table-bar">
        <AddPlayerControl />
        <BackButton fallback={paths.play} className="medieval-button">
          <span aria-hidden>←</span>
          <span className="btn-extra"> Zurück</span>
        </BackButton>
        <button type="button" className="medieval-button" onClick={handlePlayerLogout}>
          Abmelden
        </button>
      </div>
      <div className="player-table" data-testid="player-table">
        <SheetView
          data={characterSheet}
          playerLabel={primary?.name || playerName || undefined}
          hideSessionButtons
        />
        {mates.map((player) => (
          <MateSheet
            key={player.id}
            player={player}
            sheet={sheets[player.id]}
            onReload={() => setReloadToken((value) => value + 1)}
          />
        ))}
      </div>
    </div>
  );
}
